import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';

import { ReviewRating, Avatar } from '../../components';
import { userAbbreviatedName } from '../../util/data';
import { formatUSCityStateFromAddress } from '../../util/location';
import {
  normalizeInstagramProfileUrl,
  normalizeTiktokProfileUrl,
  normalizeFacebookProfileUrl,
} from '../../util/socialLinks';
import { instagramIcon } from '../PageBuilder/Primitives/Link/Icons/instagramIcon';
import { tiktokIcon } from '../PageBuilder/Primitives/Link/Icons/tiktokIcon';
import { facebookIcon } from '../PageBuilder/Primitives/Link/Icons/facebookIcon';

import css from './ListingPage.module.css';

const AVATAR_IMAGE_VARIANTS = [
  'square-xsmall',
  'square-xsmall2x',
  'square-small',
  'square-small2x',
];

const hasProfileImageUrl = (profileImage, variantNames) => {
  const variants = profileImage?.attributes?.variants;
  if (!variants) {
    return false;
  }
  return variantNames.some(name => variants[name]?.url);
};

/**
 * Resolve category display label from categoryConfiguration (by categoryLevel1, categoryLevel2, ...)
 * or fall back to listing type label from listingTypes.
 */
export const getCategoryLabel = (publicData, categoryConfiguration, listingTypes) => {
  const categories = categoryConfiguration?.categories;
  const levelKeys = categoryConfiguration?.categoryLevelKeys || [
    'categoryLevel1',
    'categoryLevel2',
    'categoryLevel3',
  ];
  if (categories?.length) {
    let options = categories;
    let label = '';
    for (const key of levelKeys) {
      const id = publicData?.[key];
      if (!id) break;
      const found = options?.find(c => c.id === id);
      if (!found) break;
      label = found.name || found.label || id;
      options = found.subcategories;
    }
    if (label) return label;
  }
  const listingType = publicData?.listingType;
  if (listingType && listingTypes?.length) {
    const config = listingTypes.find(c => c.listingType === listingType);
    return config?.label || listingType;
  }
  return publicData?.category || publicData?.listingType || '';
};

const SectionAuthorMaybe = props => {
  const { title, listing, reviews = [], categoryConfiguration, listingTypes } = props;

  if (!listing.author) {
    return null;
  }

  const publicData = listing?.attributes?.publicData || {};
  const listingMetadata = listing?.attributes?.metadata || {};
  const categoryLabel = getCategoryLabel(publicData, categoryConfiguration, listingTypes);
  const location = formatUSCityStateFromAddress(publicData.location?.address);
  const yearsExperience = publicData.yearsOfExperience;
  const businessName =
    typeof publicData.businessName === 'string' ? publicData.businessName.trim() : '';
  const instagramUrl = normalizeInstagramProfileUrl(publicData.instagramUrl || '');
  const tiktokUrl = normalizeTiktokProfileUrl(publicData.tiktokUrl || '');
  const facebookUrl = normalizeFacebookProfileUrl(publicData.facebookUrl || '');

  const reviewCount = listingMetadata?.reviewCount || 0;
  const averageRating = listingMetadata?.averageRating || 0;

  const displayName = title || listing?.attributes?.title || '';
  const hasAuthorImage = hasProfileImageUrl(listing.author?.profileImage, AVATAR_IMAGE_VARIANTS);
  const authorInitials = userAbbreviatedName(listing.author, '?');

  return (
    <section id="author" className={css.sectionAuthor}>
      <div className={css.authorHero}>
        <div className={css.authorAvatarWrapper}>
          {hasAuthorImage ? (
            <Avatar
              user={listing.author}
              disableProfileLink
              rootClassName={css.authorAvatar}
              renderSizes="152px"
            />
          ) : (
            <div className={css.authorAvatarPlaceholder}>
              <span className={css.authorAvatarPlaceholderInitials}>{authorInitials}</span>
            </div>
          )}
        </div>
        <div className={css.authorInfo}>
          <h2 className={css.authorName}>{displayName}</h2>
          {businessName ? (
            <p className={css.authorOwner}>
              <FormattedMessage id="ListingPage.ownerOf" values={{ businessName }} />
            </p>
          ) : null}
          <div className={css.authorRatingRow}>
            <ReviewRating
              rating={Math.round(averageRating)}
              className={css.authorStars}
              reviewStarClassName={css.authorStar}
            />
            <span className={css.authorRatingValue}>{averageRating.toFixed(1)}</span>
            <span className={css.authorReviewCount}>
              <FormattedMessage id="ListingPage.reviewsCount" values={{ count: reviewCount }} />
            </span>
          </div>
          <div className={css.authorBadges}>
            {categoryLabel ? <span className={css.authorBadge}>{categoryLabel}</span> : null}
            {location ? <span className={css.authorBadgePill}>{location}</span> : null}
            {yearsExperience ? (
              <span className={css.authorBadgePill}>
                <FormattedMessage
                  id="ListingPage.yearsExperience"
                  values={{ years: yearsExperience }}
                />
              </span>
            ) : null}
          </div>
          <div className={css.authorBadges}>
            {instagramUrl ? (
              <a
                className={css.authorSocialLink}
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className={css.authorSocialIcon}>
                  {instagramIcon({
                    ariaLabel: title || listing?.attributes?.title || 'Instagram',
                  })}
                </span>
                <FormattedMessage id="ListingPage.instagramLinkLabel" />
              </a>
            ) : null}
            {tiktokUrl ? (
              <a
                className={css.authorSocialLink}
                href={tiktokUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className={css.authorSocialIcon}>
                  {tiktokIcon({
                    ariaLabel: title || listing?.attributes?.title || 'TikTok',
                  })}
                </span>
                <FormattedMessage id="ListingPage.tiktokLinkLabel" />
              </a>
            ) : null}
            {facebookUrl ? (
              <a
                className={css.authorSocialLink}
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className={css.authorSocialIcon}>
                  {facebookIcon({
                    ariaLabel: title || listing?.attributes?.title || 'Facebook',
                  })}
                </span>
                <FormattedMessage id="ListingPage.facebookLinkLabel" />
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SectionAuthorMaybe;
